import { useState, useContext, useEffect } from "react";
import "./styles/profilestyle.css";
import api, { showAlert, AuthContext, API_URL } from "../util/api";
import { useNavigate } from "react-router-dom";

export default function NewDesign() {
  const navigate = useNavigate();
  const { User, Doctor, doctorFunction } = useContext(AuthContext);

  const [step, setStep] = useState(1);
  const [previewImage, setPreviewImage] = useState(null);
  const [idPreviewImage, setIdPreviewImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [available, setAvailable] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    gender: "",
    qualification: "",
    hospital: "",
    consultationFee: "",
    bankName: "",
    accountNumber: "",
    accountName: "",
    idCardType: "",
    idCardNumber: "",
    coinType: "",
    crypto_address: "",
    medical_license: "",
    bio: "",
    experience: "",
    idCardFile: null,
    profilePicture: null,
    weeklyAvailability: {},
  });

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  /* =============================
     FETCH SPECIALTIES
  ============================== */
  const checkSpecialities = async () => {
    try {
      const response = await api.get(API_URL + "/specialties");
      setAvailable(response.data.map((s) => s.name));
    } catch (err) {
      console.error("Failed to fetch specialties", err);
    }
  };

  /* =============================
     HANDLE INPUT CHANGES
  ============================== */
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files[0]) {
      const reader = new FileReader();
      reader.onload = () => {
        if (name === "profilePicture") setPreviewImage(reader.result);
        if (name === "idCardFile") setIdPreviewImage(reader.result);
      };
      reader.readAsDataURL(files[0]);
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  /* =============================
     HANDLE WEEKLY AVAILABILITY
  ============================== */
  const handleAvailabilityChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      weeklyAvailability: {
        ...prev.weeklyAvailability,
        [day]: {
          ...prev.weeklyAvailability[day],
          [field]: value,
        },
      },
    }));
  };

  /* =============================
     SPECIALITIES
  ============================== */
  const addSpeciality = (item) => {
    if (!selected.includes(item)) {
      setSelected([...selected, item]);
      setSearch("");
    }
  };

  const removeSpeciality = (item) => {
    setSelected(selected.filter((s) => s !== item));
  };

  /* =============================
     VALIDATION
  ============================== */
  const validateCurrentStep = () => {
    const container = document.querySelector(`.step-${step}`);
    if (!container) return true;

    const requiredFields = container.querySelectorAll(
      "input[required], select[required], textarea[required]"
    );

    let firstInvalid = null;

    requiredFields.forEach((field) => {
      field.classList.remove("is-invalid");

      const isEmpty =
        field.type === "checkbox"
          ? !field.checked
          : !field.value?.toString().trim();

      if (isEmpty) {
        field.classList.add("is-invalid");
        if (!firstInvalid) firstInvalid = field;
      }
    });

    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      firstInvalid.focus();
      return false;
    }

    return true;
  };

  const nextStep = () => {
    if (validateCurrentStep()) setStep((prev) => prev + 1);
  };

  const prevStep = () => setStep((prev) => prev - 1);

  /* =============================
     HANDLE SUBMIT
  ============================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setProgress(10);

    const payload = new FormData();

    for (let key in formData) {
      if (key === "weeklyAvailability") {
        payload.append(
          key,
          JSON.stringify(
            Object.entries(formData.weeklyAvailability).map(([day, v]) => ({
              day,
              available: !!v.available,
              startTime: v.startTime || null,
              endTime: v.endTime || null,
            }))
          )
        );
      } else {
        payload.append(key, formData[key]);
      }
    }

    payload.append("speciality", JSON.stringify(selected));
    payload.append("user_id", User.id);

    try {
      const res = await api.post("/consultant/register", payload, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) =>
          setProgress(Math.round((e.loaded / e.total) * 100)),
      });

      if (res.data.status === "success") {
        doctorFunction(res.data.doctor, res.data.user);
        showAlert("success", "Success", res.data.message);
        navigate("/doctor-profile/" + res.data.doctor.id);
      } else {
        showAlert("error", "Error", res.data.message);
      }
    } catch (err) {
      showAlert("error", "Error", err.message);
    } finally {
      setSubmitting(false);
      setTimeout(() => setProgress(0), 1500);
    }
  };

  /* =============================
     POPULATE FORM FROM DOCTOR
  ============================== */
  useEffect(() => {
    checkSpecialities();

    if (!Doctor) return;

    // Weekly availability
    const updatedWeekly = {};
    if (Doctor.weekly_availabilities) {
      Doctor.weekly_availabilities.forEach((item) => {
        updatedWeekly[item.day] = {
          available: item.available,
          startTime: item.start_time || "",
          endTime: item.end_time || "",
        };
      });
    }

    // Merge doctor data into formData
    setFormData((prev) => ({
      ...prev,
      name: Doctor.name || "",
      phone: Doctor.phone || "",
      gender: Doctor.gender || "",
      qualification: Doctor.qualification || "",
      hospital: Doctor.hospital || "",
      /* consultationFee: Doctor.consultationFee || "", */
      bankName: Doctor.bankName || "",
      accountNumber: Doctor.accountNumber || "",
      accountName: Doctor.accountName || "",
      idCardType: Doctor.idCardType || "",
      idCardNumber: Doctor.idCardNumber || "",
      coinType: Doctor.coinType || "",
      crypto_address: Doctor.crypto_address || "",
      medical_license: Doctor.medical_license || "",
      bio: Doctor.bio || "",
      experience: Doctor.experience || "",
      profilePicture: Doctor.profilePicture || null,
      idCardFile: Doctor.idCardFile || null,
      weeklyAvailability: updatedWeekly,
    }));

    setPreviewImage(Doctor.profilePicture || null);
    setIdPreviewImage(Doctor.idCardFile || null);

    // Specialties
    try {
      setSelected(JSON.parse(Doctor.speciality || "[]"));
    } catch (err) {
      console.error("Failed to parse specialties", err);
    }
  }, [Doctor]);

  /* =============================
     FILTER SPECIALTIES
  ============================== */
  useEffect(() => {
    setFiltered(
      available.filter(
        (s) => !selected.includes(s) && s.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [search, selected, available]);


  return (
    <div className="container-fluid form-container">
      <div className="form-card-modern">
        <h2 className="form-title">Doctor Profile Setup</h2>

        {/* Progress Bar */}
        <div className="progressbar">
          <div style={{ cursor: "pointer" }} onClick={() => setStep(1)} >  <span className={`step ${step >= 1 ? "active" : ""}`}>1 </span>  <span>Personal Info</span> </div>
          <div style={{ cursor: "pointer" }} onClick={() => setStep(2)} > <span className={`step ${step >= 2 ? "active" : ""}`}>2 </span>  <span>Professional Info</span> </div>
          {/*   <div style={{ cursor: "pointer" }} onClick={() => setStep(3)} > <span className={`step ${step >= 3 ? "active" : ""}`}>3 </span>  <span>Consultation Fee</span> </div> */}
          <div style={{ cursor: "pointer" }} onClick={() => setStep(3)} > <span className={`step ${step >= 3 ? "active" : ""}`}>3 </span>  <span>Availability</span> </div>

        </div>

        <form onSubmit={handleSubmit}>
          {/* ------------------ STEP 1 ------------------ */}
          {step === 1 && (
            <div className="step-1 step-section">
              <h4>Personal & Professional</h4>
              <div className="profile-upload-modern">
                <img
                  src={previewImage || "/logo_back_remove.png"}
                  alt="profile"
                />
                <label htmlFor="profilePicture" className="upload-label">
                  📷 Upload
                </label>
                <input
                  type="file"
                  id="profilePicture"
                  name="profilePicture"
                  hidden
                  onChange={handleChange}
                />
              </div>
              <label>Email</label>
              <input readOnly value={User.email} className="readonly-input" />
              <label>Full Name</label>
              <input
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Full Name"
              />
              <label>Phone</label>
              <input
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone"
              />
              <label>Gender</label>
              <select
                name="gender"
                required
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Gender</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>

              <label>Specialties</label>

              {/* Specialties */}
              <div className="speciality-section">
                <input
                  type="text"
                  placeholder="Search specialties..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="speciality-dropdown">
                  {filtered.length === 0 && <p>No match found</p>}
                  {filtered.map((s) => (
                    <div
                      key={s}
                      onClick={() => addSpeciality(s)}
                      className="speciality-item"
                    >
                      {s}
                    </div>
                  ))}
                </div>
                <div className="selected-specialities">
                  {selected.map((s) => (
                    <span key={s} className="badge">
                      {s}{" "}
                      <button onClick={() => removeSpeciality(s)}>&times;</button>
                    </span>
                  ))}
                </div>
              </div>

              <label>Qualification</label>

              <input
                name="qualification"
                required
                value={formData.qualification}
                onChange={handleChange}
                placeholder="Qualification"
              />

              <label>Medical License</label>
              <input
                name="medical_license"
                required
                value={formData.medical_license}
                onChange={handleChange}
                placeholder="Medical License"
              />

              <label>Experience</label>
              <input
                name="experience"
                type="number"
                required
                value={formData.experience}
                onChange={handleChange}
                placeholder="Experience (Years)"
              />
              <label>Bio</label>
              <textarea
                name="bio"
                required
                value={formData.bio}
                onChange={handleChange}
                placeholder="About You"
              />

              <button type="button" className="action-button" onClick={nextStep}>
                Continue
              </button>
            </div>
          )}

          {/* ------------------ STEP 2 ------------------ */}
          {step === 2 && (
            <div className="step-2 step-section">
              <h4>KYC</h4>

              <label>Hospital / Clinic</label>
              <input
                name="hospital"
                value={formData.hospital}
                onChange={handleChange}
                placeholder="Hospital / Clinic"
              />

              <label>ID Card</label>
              <select
                name="idCardType"
                value={formData.idCardType}
                onChange={handleChange}
              >
                <option value="">ID Type</option>
                <option>National ID</option>
                <option>Passport</option>
                <option>Driver’s License</option>
              </select>

              <label>ID Number</label>
              <input
                name="idCardNumber"
                value={formData.idCardNumber}
                onChange={handleChange}
                placeholder="ID Number"
              />
              <label>Upload ID Card</label>
              <input type="file" name="idCardFile" onChange={handleChange} />
              {idPreviewImage && <img src={idPreviewImage} alt="" style={{ objectFit: "contain", width: "160px" }} />}
              <div className="step-nav">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                &nbsp;
                <button type="button" onClick={nextStep} className="action-button">
                  Continue
                </button>
              </div>
            </div>
          )}


          {/* ------------------ STEP 3 ------------------ */}
          {/*    {step === 3 && (
            <div className="step-3 step-section">
              <h4>Payment Info</h4>
              <label>Consultation Fee</label>
              <input
                name="consultationFee"
                required
                value={formData.consultationFee}
                onChange={handleChange}
                placeholder="Consultation Fee"
              />
              

              <div className="step-nav">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                &nbsp;
                <button type="button" onClick={nextStep} className="action-button">
                  Continue
                </button>
              </div>
            </div>
          )} */}



          {/* ------------------ STEP 4 ------------------ */}
          {step === 3 && (
            <div className="step-4 step-section">
              <h4>Weekly Availability</h4>
              <div className="availability-grid">
                {daysOfWeek.map((day) => (
                  <div key={day} className="availability-card-modern">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={formData.weeklyAvailability[day]?.available || false}
                        onChange={(e) =>
                          handleAvailabilityChange(day, "available", e.target.checked)
                        }
                      />
                      <span className="slider round"></span>
                    </label>
                    <span>{day}</span>
                    {formData.weeklyAvailability[day]?.available && (
                      <div className="time-inputs">
                        <input
                          type="time"
                          value={formData.weeklyAvailability[day]?.startTime || ""}
                          onChange={(e) =>
                            handleAvailabilityChange(day, "startTime", e.target.value)
                          }
                        />
                        <input
                          type="time"
                          value={formData.weeklyAvailability[day]?.endTime || ""}
                          onChange={(e) =>
                            handleAvailabilityChange(day, "endTime", e.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {submitting && (
                <div className="progress">
                  <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                  <p>{progress}%</p>
                </div>
              )}

              <div className="step-nav">
                <button type="button" onClick={prevStep} className="back-btn">
                  Back
                </button>
                &nbsp;
                <button type="submit" disabled={submitting} className="action-button">
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
